/**
 * Labstep
 *
 * @module screens/ProtocolCollection/Show/Center
 * @desc Center Component of Protocol Show
 * Shows alert if the user cannot edit this version
 */

import { EntityPrimaryInfo } from 'labstep-web/components/Entity/PrimaryInfo';
import { EntityUserCard } from 'labstep-web/components/EntityUser/Card';
import FolderManager from 'labstep-web/components/Folder/Manager';
import { ProtocolActionCreate } from 'labstep-web/components/Protocol/Action/Create';
import { ProtocolActionDelete } from 'labstep-web/components/Protocol/Action/Delete';
import { ProtocolActionRun } from 'labstep-web/components/Protocol/Action/Run';
import { ProtocolActionUpdateIsDraft } from 'labstep-web/components/Protocol/Action/UpdateIsDraft';
import { ProtocolChangelog } from 'labstep-web/components/Protocol/Changelog';
import { ProtocolFormShowEditStateContainer as ProtocolFormShowEditState } from 'labstep-web/components/Protocol/Form/ShowEdit/State/Container';
import { ProtocolLabelStatus } from 'labstep-web/components/Protocol/Label/Status';
import {
  Action,
  ProtocolCollectionActionMenu,
} from 'labstep-web/components/ProtocolCollection/Action/Menu';
import TagManager from 'labstep-web/components/Tag/Manager';
import { ICONS } from 'labstep-web/constants/icons';
import { InformationCard } from 'labstep-web/core/Card/Information';
import { Flex } from 'labstep-web/core/Flex';
import { Identifier } from 'labstep-web/core/Identifier';
import { Link } from 'labstep-web/core/Link';
import { ProtocolCollection } from 'labstep-web/models/protocol-collection.model';
import type React from 'react';
import { Message } from 'semantic-ui-react';
import styles from './styles.module.scss';
import type { ICenterProps } from './types';

export const ProtocolCollectionShowCenter: React.FC<ICenterProps> = ({
  protocolCollection,
}) => {
  let banner;
  const protocol = protocolCollection.last_or_draft_version;
  if (protocol.is_draft) {
    banner = (
      <Message
        children={
          <div className={styles.banner}>
            <span>
              You are viewing a draft version of this{' '}
              {ProtocolCollection.getHumanReadableEntityName(
                false,
                false,
                protocolCollection.is_template,
              )}
            </span>
            {protocolCollection.last_version && (
              <Link
                to="protocol_show"
                params={{ id: protocolCollection.last_version?.id }}
              >
                View Live Version
              </Link>
            )}
          </div>
        }
      />
    );
  }

  const actions = protocolCollection.is_template
    ? [
        Action.duplicate,
        Action.print,
        Action.transfer_ownership,
        Action.protocol_from_template,
        Action.share,
        Action.allowed_actions_lock_settings,
        Action.delete,
      ]
    : [
        Action.add_to_folder,
        Action.tag,
        Action.add_to_experiment,
        Action.duplicate,
        Action.print,
        Action.delete,
        Action.share,
        Action.save_as_template,
        Action.transfer_ownership,
        Action.allowed_actions_lock_settings,
        Action.reporting,
      ];

  return (
    <div className={styles.container}>
      <InformationCard
        additionalActions={
          <>
            <EntityUserCard entity={protocolCollection} />
            <FolderManager parentEntity={protocolCollection} />
            <TagManager parentEntity={protocolCollection} />
          </>
        }
        actionMenu={
          <ProtocolCollectionActionMenu
            protocolCollection={protocolCollection}
            actions={actions}
          />
        }
        primaryInfo={
          <EntityPrimaryInfo
            entity={protocolCollection}
            icon={ICONS.protocol_collection.primary}
            name={protocolCollection.name}
            prefix={
              protocolCollection.custom_identifier && (
                <>
                  <Identifier>
                    {protocolCollection.custom_identifier}
                  </Identifier>
                  -
                </>
              )
            }
          />
        }
        secondaryInfo={
          <Flex vAlignContent="center" wrap spacing="gap">
            <ProtocolChangelog
              protocol={protocol}
              protocolCollection={protocolCollection}
            />
            {protocol.is_draft ? (
              <Flex vAlignContent="center" spacing="gap">
                <ProtocolLabelStatus protocol={protocol} />
                <ProtocolActionUpdateIsDraft
                  protocol={protocol}
                  protocolCollection={protocolCollection}
                />
                <ProtocolActionDelete protocol={protocol} />
              </Flex>
            ) : (
              <>
                <ProtocolLabelStatus protocol={protocol} />
                <ProtocolActionCreate
                  parentName={protocolCollection.entityName}
                  parentId={protocolCollection.id}
                  actionComponentProps={{
                    type: 'text',
                    icon: 'edit',
                    text: 'Draft New Version',
                  }}
                />
              </>
            )}
          </Flex>
        }
        bottom={
          <>
            {protocolCollection.is_template && (
              <Message
                data-cy="components/ExperimentWorkflowTemplate/Warning"
                color="yellow"
              >
                This is a template.
              </Message>
            )}
            {banner}
            {!protocolCollection.is_template && (
              <Flex hAlignContent="right">
                <ProtocolActionRun protocol={protocol} />
              </Flex>
            )}
            <ProtocolFormShowEditState
              protocolCollection={protocolCollection}
              protocol={protocol}
            />
          </>
        }
      />
    </div>
  );
};

export default ProtocolCollectionShowCenter;
